import { useMemo, useCallback } from 'react';
import { useAppSelector } from 'modules/state-mgmt/store';
import { IAddress } from 'services/GoogleMapsService';
import { WorkerProfileField } from 'types/profileCustomFields';
import { shouldShowStateForCountry, shouldShowCountyForCountry } from 'utils/addressValidationUtils';

import { IContactFormState } from '../types';

interface IOption {
  value: string;
  label: string;
}

interface IUseContactAddressLogicReturn {
  countryOptions: IOption[];
  selectedCountryOption: IOption | null;
  stateOptions: IOption[];
  selectedStateOption: IOption | null;
  handleCountryChange: (newValue: any) => void;
  handleStateChange: (newValue: any) => void;
  shouldShowState: boolean;
  shouldShowCounty: boolean;
  shouldShowBorough: boolean;
  handleAddressSelect: (address: IAddress) => void;
  allowedCountryCodes: string[];
}

export const useContactAddressLogic = (
  contactForm: IContactFormState,
  onFieldChange: (field: WorkerProfileField, value: string) => void,
  onDirectFieldUpdate?: (fieldName: keyof IContactFormState, value: string) => void,
): IUseContactAddressLogicReturn => {
  const { countries, states, language } = useAppSelector(state => ({
    countries: state?.cache?.countries || {},
    states: state?.cache?.states || {},
    language: state?.app?.languageCode || 'en',
  }));

  // Get localized data with proper fallback
  const currentLanguageCountries = useMemo(() => countries[language] || [], [countries, language]);
  const currentLanguageStates = useMemo(() => states[language] || [], [states, language]);

  // Country options and selection
  const countryOptions = useMemo(
    () =>
      currentLanguageCountries.map((country: any) => ({
        value: country.id,
        label: country.name,
      })),
    [currentLanguageCountries],
  );

  const selectedCountryOption = useMemo(() => {
    const country = currentLanguageCountries.find((c: any) => c.id === contactForm.country);
    return country ? { value: country.id, label: country.name } : null;
  }, [currentLanguageCountries, contactForm.country]);

  // Current selected country for state filtering and conditional logic
  const selectedCountry = useMemo(
    () => currentLanguageCountries.find((c: any) => c.id === contactForm.country),
    [currentLanguageCountries, contactForm.country],
  );

  // State options and selection (only for countries that have states)
  const stateOptions = useMemo(() => {
    if (!selectedCountry) return [];
    // Build a set of possible country identifiers (names/codes/ids) across all cached languages
    const possibleCountryIds = new Set<string>();
    possibleCountryIds.add(selectedCountry.name);
    possibleCountryIds.add(selectedCountry.id);
    possibleCountryIds.add(selectedCountry.code);
    Object.keys(countries).forEach(lang => {
      const langCountries: any[] = countries[lang] || [];
      const match = langCountries.find(c => c.id === selectedCountry.id);
      if (match) {
        possibleCountryIds.add(match.name);
        possibleCountryIds.add(match.code);
        possibleCountryIds.add(match.id);
      }
    });

    return currentLanguageStates
      .filter((state: any) => possibleCountryIds.has(state.country))
      .map((state: any) => ({
        value: state.code,
        label: state.name,
      }));
  }, [selectedCountry, currentLanguageStates]);

  const selectedStateOption = useMemo(() => {
    return stateOptions.find((option: IOption) => option.value === contactForm.state) || null;
  }, [stateOptions, contactForm.state]);

  // Conditional field visibility
  const shouldShowState = useMemo(() => {
    return shouldShowStateForCountry(selectedCountry?.code);
  }, [selectedCountry]);

  const shouldShowCounty = useMemo(() => {
    return shouldShowCountyForCountry(selectedCountry?.code);
  }, [selectedCountry]);

  // Borough is only shown when state is New York (NY) AND there's a value
  const shouldShowBorough = useMemo(() => {
    return contactForm.state === 'NY' && !!contactForm.borough;
  }, [contactForm.state, contactForm.borough]);

  // Google Places allowed countries
  const allowedCountryCodes = useMemo(
    () => currentLanguageCountries.map((country: any) => country.code),
    [currentLanguageCountries],
  );

  // Event handlers
  const handleCountryChange = useCallback(
    (newValue: any) => {
      onFieldChange(WorkerProfileField.homeAddressCountry, newValue?.value || '');
      onFieldChange(WorkerProfileField.homeAddressStateProvince, ''); // Reset state when country changes

      // Update country name for API
      if (onDirectFieldUpdate) {
        onDirectFieldUpdate('countryName', newValue?.label || '');
        onDirectFieldUpdate('stateName', ''); // Reset state name when country changes
        onDirectFieldUpdate('borough', ''); // Reset borough when country changes
        onDirectFieldUpdate('latitude', undefined as any); // Reset latitude when country changes
        onDirectFieldUpdate('longitude', undefined as any); // Reset longitude when country changes
      }
    },
    [onFieldChange, onDirectFieldUpdate],
  );

  const handleStateChange = useCallback(
    (newValue: any) => {
      onFieldChange(WorkerProfileField.homeAddressStateProvince, newValue?.value || '');

      // Update state name for API
      if (onDirectFieldUpdate && newValue) {
        const selectedState = stateOptions.find((option: IOption) => option.value === newValue.value);
        onDirectFieldUpdate('stateName', selectedState?.label || '');
      }

      // Clear borough if state is no longer New York
      if (onDirectFieldUpdate && newValue?.value !== 'NY') {
        onDirectFieldUpdate('borough', '');
        onDirectFieldUpdate('latitude', undefined as any);
        onDirectFieldUpdate('longitude', undefined as any);
      }
    },
    [onFieldChange, onDirectFieldUpdate, stateOptions],
  );

  // Google Places address selection
  const handleAddressSelect = useCallback(
    (address: IAddress) => {
      const selectedCountry = currentLanguageCountries.find((c: any) => c.code === address.countryCode);
      const selectedState = selectedCountry
        ? currentLanguageStates.find((state: any) => {
            if (state.code !== address.stateCode) return false;
            // Build identifiers similar to stateOptions logic
            const possibleCountryIds = new Set<string>();
            possibleCountryIds.add(selectedCountry.name);
            possibleCountryIds.add(selectedCountry.id);
            possibleCountryIds.add(selectedCountry.code);
            Object.keys(countries).forEach(lang => {
              const langCountries: any[] = countries[lang] || [];
              const match = langCountries.find(c => c.id === selectedCountry.id);
              if (match) {
                possibleCountryIds.add(match.name);
                possibleCountryIds.add(match.code);
                possibleCountryIds.add(match.id);
              }
            });

            return possibleCountryIds.has(state.country);
          })
        : null;

      // Update form fields
      onFieldChange(WorkerProfileField.homeAddressLine1, address.addressLine1 || '');
      onFieldChange(WorkerProfileField.homeAddressLine2, address.addressLine2 || '');
      onFieldChange(WorkerProfileField.homeAddressCity, address.city || '');
      onFieldChange(WorkerProfileField.homeAddressZipPostal, address.postalCode || '');
      onFieldChange(WorkerProfileField.homeAddressCountry, selectedCountry?.id || '');
      onFieldChange(WorkerProfileField.homeAddressStateProvince, selectedState?.code || '');

      // Update additional API fields
      if (onDirectFieldUpdate) {
        if (address.county) onDirectFieldUpdate('county', address.county);
        if (address.borough) onDirectFieldUpdate('borough', address.borough);
        if (address.latitude !== undefined) onDirectFieldUpdate('latitude', address.latitude as any);
        if (address.longitude !== undefined) onDirectFieldUpdate('longitude', address.longitude as any);
        if (selectedCountry) onDirectFieldUpdate('countryName', selectedCountry.name);
        if (selectedState) onDirectFieldUpdate('stateName', selectedState.name);
      }
    },
    [currentLanguageCountries, currentLanguageStates, onFieldChange, onDirectFieldUpdate],
  );

  return {
    countryOptions,
    selectedCountryOption,
    stateOptions,
    selectedStateOption,
    handleCountryChange,
    handleStateChange,
    handleAddressSelect,
    shouldShowState,
    shouldShowCounty,
    shouldShowBorough,
    allowedCountryCodes,
  };
};
